import { useEffect, useState } from "react";
import Button from "../../components/Button";
import Layout from "../../layout";
import { useCollections } from "../../hooks/useCollectionList";
import CollectionList from "../Home/components/CollectionList";
import { useSearchParams } from "react-router-dom";
import Loading from "../../components/Loading";
import PageHeader from "../../components/PageHeader";
import { Park } from "@mui/icons-material";
import { readScratchCollection } from "../../utils/scratch";

const filters = [
  {
    id: "all",
    title: "Todas",
  },
  {
    id: "lastWeek",
    title: "Última semana",
  },
  {
    id: "lastMonth",
    title: "Último mês",
  },
];

function scratchMatchesFilter(scratch: any, filterId: string) {
  if (filterId === "all") {
    return true;
  }
  const rawDate = scratch?.forms?.date || scratch?.createdAt;
  if (!rawDate) {
    return false;
  }
  const collectionDate = new Date(rawDate);
  if (Number.isNaN(collectionDate.getTime())) {
    return false;
  }
  const currentDate = new Date();
  if (filterId === "lastWeek") {
    const diffInDays = Math.floor(
      (currentDate.getTime() - collectionDate.getTime()) /
        (1000 * 60 * 60 * 24)
    );
    return diffInDays < 7;
  }
  if (filterId === "lastMonth") {
    const lastMonth = new Date(currentDate);
    lastMonth.setMonth(currentDate.getMonth() - 1);
    return collectionDate >= lastMonth && collectionDate < currentDate;
  }
  return false;
}

function withScratch(
  collections: any[],
  filterId: string,
  praca?: string | null
) {
  const scratch = readScratchCollection();
  if (!scratch || !scratchMatchesFilter(scratch, filterId)) {
    return collections;
  }
  if (praca && scratch.forms?.property !== praca) {
    return collections;
  }
  return [scratch, ...collections];
}

export default function MinhasAvaliacoesPage() {
  const [selectedItem, setSelectedItem] = useState({
    id: "all",
    title: "Todas",
  });
  const [collections, setCollections] = useState<any[]>([]);
  const { collections: collectionList } = useCollections();
  const [searchParams] = useSearchParams();
  const praca = searchParams.get("praca");

  const applyFilter = (id: string, list: any[]) => {
    const currentDate = new Date();
    let filteredCollections = list;

    if (id === "lastWeek") {
      filteredCollections = list.filter((collection) => {
        const collectionDate = new Date(collection.createdAt);
        const diffInDays = Math.floor(
          (currentDate.getTime() - collectionDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );
        return diffInDays < 7;
      });
    }

    if (id === "lastMonth") {
      filteredCollections = list.filter((collection) => {
        const collectionDate = new Date(collection.createdAt);
        const lastMonth = new Date(currentDate);
        lastMonth.setMonth(currentDate.getMonth() - 1);

        return collectionDate >= lastMonth && collectionDate < currentDate;
      });
    }

    if (praca) {
      filteredCollections = filteredCollections.filter((collection) => {
        return collection.forms?.property === praca;
      });
    }

    setCollections(withScratch(filteredCollections, id, praca));
  };

  const handleChangeFilter = (id: any, title: any) => {
    setSelectedItem({
      id,
      title,
    });
    applyFilter(id, collectionList ?? []);
  };

  useEffect(() => {
    applyFilter(selectedItem.id, collectionList ?? []);
    // Filter identity is selectedItem.id; applyFilter reads current lists from this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionList, praca, selectedItem.id]);

  if (!collectionList) {
    return (
      <Layout>
        <Loading label="Carregando avaliações…" />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title="Minhas avaliações"
        description={
          praca
            ? `Qualificações da praça ${praca}.`
            : "Filtre pelo período da visita e abra qualquer avaliação."
        }
      />

      <div
        className="mb-6 flex flex-wrap gap-2"
        role="group"
        aria-label="Filtrar por período"
      >
        {filters?.map((item: any) => {
          const isSelected = item.id === selectedItem.id;
          return (
            <Button
              key={`filter-button-${item.id}`}
              className="!w-auto"
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => handleChangeFilter(item.id, item.title)}
              aria-pressed={isSelected}
            >
              {item?.title}
            </Button>
          );
        })}
      </div>

      <CollectionList
        collections={collections}
        emptyIcon={<Park />}
        emptyTitle={
          selectedItem.id === "all" && !praca
            ? "Nenhuma avaliação ainda"
            : "Nada neste recorte"
        }
        emptyDescription={
          selectedItem.id === "all" && !praca
            ? "Comece cadastrando a praça visitada. O rascunho é salvo automaticamente enquanto você preenche."
            : "Não há avaliações para o período ou praça selecionados."
        }
      />
    </Layout>
  );
}
